import React, {useEffect} from "react";
import {ContentModelName} from "../contenful/types";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../RootReducer";
import {fetchAndParseContent, fetchAndParseContentByMode} from "../contenful/ContentfulActions";
import {Spinner} from "../ui/Spinner";
import LandingPageComponent from "./LandingPageComponent";
import {fetchSettings} from "../settings/SettingsActions";

import {AppMode} from "../http/protocol";
import {getContainer} from "../ioc/IOCSetup";
import {Helmet} from "react-helmet";
import {t} from "../i18n";
import MetaHelmet from "../utils/MetaHelmet";
import "./styles/LandingPage.scss"
import {isLandingPage} from "../navigation/NavigationActions";
import {ContentStatus} from "../contenful/ContentfulReducer";
import { getStatusForContent } from "../selectors/content-selectors";
import { getLoggedIn } from "../selectors/user-selectors";
import { getSettings } from "../selectors/settings-selectors";
import { formatUrl } from "../utils/UrlUtils";

export const LandingPage = () => {
  const landingPageStatus = useAppSelector((s) => getStatusForContent(s, ContentModelName.LANDINGPAGE));
  const onboardingPageStatus = useAppSelector((s) => getStatusForContent(s, ContentModelName.ONBOARDINGPAGE));
  const landingpage = useAppSelector((s) => s.contentful.landingpage);
  const locale = getContainer().getSettingsService().getActiveLocale().code;
  const loggedIn = useAppSelector((s) => getLoggedIn(s));
  const settings = useAppSelector((s) => getSettings(s));
  const appMode = useAppSelector((s) => s.user.appMode);
  const pathPush: string = appMode == AppMode.NONE ? "/playnow" : "/app";
  const title: string = (settings && settings.title) || "";
  const dispatch = useDispatch();
  const settingsService = getContainer().getSettingsService();
  const alternateLang = settingsService
    .getLocalesData()
    .map(({ code }) => code)
    .filter((code) => code !== locale);
  const subtitle: string = t("meta.landingpage.title") || "";
  const fullTitle: string = `${title}${subtitle.length > 0 ? " - " + subtitle : ""}`;
  const description: string = t("meta.landingpage.description") || "";
  const keywords: string = t("meta.landingpage.keywords") || "";
  const baserHref: string = (settings?.canonicalBaseUrl ? settings.canonicalBaseUrl : window.location.origin) + "/";
  const seo = {
    title: landingpage?.seo?.[0]?.title,
    description: landingpage?.seo?.[0]?.description,
    imageURL: formatUrl(landingpage?.seo?.[0]?.image?.fields.file.url),
  };

  useEffect(() => {
    dispatch(isLandingPage(true));

    if (landingPageStatus == ContentStatus.NONE && appMode) {
      dispatch(fetchAndParseContentByMode(ContentModelName.LANDINGPAGE, appMode, locale) as any);
    }

    if (onboardingPageStatus == ContentStatus.NONE) {
      dispatch(fetchAndParseContent(ContentModelName.ONBOARDINGPAGE, locale) as any);
    }
    if (!settings) {
      dispatch(fetchSettings() as any);
    }
    return () => {
      dispatch(isLandingPage(false));
    };
  }, [dispatch]);

  return (
    <div style={{ overflowX: "hidden" }} className={loggedIn ? `landingPage logged-in` : `landingPage`}>
      <MetaHelmet title={fullTitle} description={description} keywords={keywords} ogTitle={seo.title} ogDescription={seo.description} ogImage={seo.imageURL} lang={locale} />
      <Helmet>
        <link href={baserHref + locale + "/"} rel="canonical"/>
        {alternateLang.map((lang) => (
            <link key={`link-${lang}`} rel={"alternate"} href={baserHref + `${lang}/`} hrefLang={lang}/>
        ))}
        <link rel={"alternate"} href={baserHref} hrefLang="x-default"/>
      </Helmet>
      {landingPageStatus != ContentStatus.SUCCESS ? <Spinner/> : landingpage &&
        <LandingPageComponent loggedIn={loggedIn} settings={settings} landingpage={landingpage} pathPush={pathPush} />}
    </div>
  );
};

export default LandingPage;